import React, { useRef, useState, useEffect } from "react";
import * as tf from "@tensorflow/tfjs";
import CheckupQuiz from "./CheckupQuiz"; // Importiere das Quiz-Component
// import Sidebar from "./Sidebar"; // falls benötigt

const MODEL_URL_LEVEL6 = "https://teachablemachine.withgoogle.com/models/ugFL3oxKT/model.json"; // Ersetze ggf. durch dein Modell
const METADATA_URL_LEVEL6 = "https://teachablemachine.withgoogle.com/models/ugFL3oxKT/metadata.json"; // Ersetze ggf. durch deine Metadaten

const LEVEL6_PASSWORD = "KLASSIFIZIEREN";

type Prediction = {
    label: string;
    confidence: number;
};

type Props = {
    onRestart: () => void;
    onNextLevel: () => void;
    avatarImage: string;
    groupName: string;
    onBack?: () => void; // für Abbrechen im Passwort-Modal
    unlocked?: boolean; // für Passwort-Logik
    unlockLevel?: () => void; // für Passwort-Logik
};

export default function Level6({ onRestart, onNextLevel, avatarImage, groupName, onBack, unlocked, unlockLevel }: Props) {
    const videoRef = useRef<HTMLVideoElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [model, setModel] = useState<tf.LayersModel | null>(null);
    const [labels, setLabels] = useState<string[]>([]);
    const [loading, setLoading] = useState(true);
    const [videoReady, setVideoReady] = useState(false);

    const [showTestModal, setShowTestModal] = useState(false);
    const [showResultModal, setShowResultModal] = useState(false);
    const [currentPredictions, setCurrentPredictions] = useState<Prediction[]>([]);
    const [isTestImageCaptured, setIsTestImageCaptured] = useState(false); // Nicht direkt verwendet, aber kann nützlich sein
    const [capturedImageUrl, setCapturedImageUrl] = useState<string | null>(null); // Hinzugefügt, um das Bild zu speichern
    const [showAnalyzeModal, setShowAnalyzeModal] = useState(false);

    // Phasensteuerung für Level 6
    // 0: Einführungstext (Frame 29)
    // 1: Anweisung für Testfoto (Frame 30)
    // 2: Testfoto Modal offen (Frame 31)
    // 3: Ergebnis Modal offen (Frame 58 & 32)
    // 4: "Wusstet ihr schon?" Slide (Frame 33)
    // 5: Quiz Intro (Testet euer Wissen!) (Basierend auf Frame 34 & 24)
    // 6: Quiz anzeigen (Frame 34 & 24)
    // 7: Abschluss
    const [levelStep, setLevelStep] = useState(0);


    // Texte und Fragen basierend auf Screenshots
    const knewItTextLevel6 = {
        question: "Was ist ein neuronales Netzwerk?",
        explanation: `Ich habe ein schlaues Computer-Gehirn, das Bilder erkennen kann. 💻🧠 Das nennt man ein neuronales Netzwerk. Viele kleine Helfer, die Neuronen heißen, schauen sich zusammen das Bild an. Sie achten auf Farben, Formen und Konturen auch wenn der Apfel schon angebissen ist. Dann reden sie miteinander und überlegen, ob es ein Apfel oder eine Banane ist.`
    };

    const quizQuestion1 = {
        question: "Warum kann KIKI auch einen angebissenen Apfel oder nur den Umriss erkennen?",
        options: [
            { label: "A) Weil sie den Geschmack kennt", value: "A" },
            { label: "B) Weil sie rät, was es sein könnte", value: "B" },
            { label: "C) Weil KIKI sich die Form und wichtige Punkte vom Apfel merkt", value: "C" },
        ],
        correctAnswer: "C", // Annahme basierend auf "Umriss" und "neuronales Netzwerk" Erklärung
        explanation: "Eine KI lernt aus den Merkmalen und Umrissen von Objekten. Wenn sie genug Beispiele von Äpfeln in verschiedenen Zuständen (ganz, angebissen) gesehen hat, kann sie diese Merkmale wiedererkennen.",
    };

    const quizQuestion2 = {
        question: "Warum ist es wichtig, dass KIKI auch Äpfel mit veränderter Form oder nur als Umriss erkennen kann?",
        options: [
            { label: "A) Weil es in der echten Welt viele verschiedene Apfel-Bilder gibt", value: "A" },
            { label: "B) Weil Äpfel immer gleich aussehen", value: "B" },
            { label: "C) Weil KIKI sonst lieber Bananen isst", value: "C" },
        ],
        correctAnswer: "A",
        explanation: "In der realen Welt sieht ein Apfel nicht immer gleich aus. Er kann angebissen, halbiert oder im Schatten sein. Eine Künstliche Intelligenz muss lernen, ihn trotzdem zu erkennen.",
    };

    useEffect(() => {
        async function loadModelAndMetadata() {
            setLoading(true);
            try {
                const loadedModel = await tf.loadLayersModel(MODEL_URL_LEVEL6);
                setModel(loadedModel);
                const metadataResponse = await fetch(METADATA_URL_LEVEL6);
                const metadata = await metadataResponse.json();
                setLabels(metadata.labels);
            } catch (err) {
                console.error("Fehler beim Laden des Modells oder der Metadaten in Level 6:", err);
            } finally {
                setLoading(false);
            }
        }
        loadModelAndMetadata();
    }, []);

    useEffect(() => {
        if (!showTestModal) {
            setVideoReady(false);
            if (videoRef.current?.srcObject) {
                (videoRef.current.srcObject as MediaStream)
                    .getTracks()
                    .forEach((track) => track.stop());
            }
            return;
        }

        async function setupCamera() {
            if (!videoRef.current) return;
            try {
                const stream = await navigator.mediaDevices.getUserMedia({ video: true });
                videoRef.current.srcObject = stream;
                videoRef.current.onloadedmetadata = () => {
                    if (videoRef.current) {
                        videoRef.current.play();
                        setVideoReady(true);
                    }
                };
            } catch (err) {
                console.error("Fehler beim Zugriff auf die Kamera in Level 6:", err);
                setVideoReady(false);
            }
        }

        setupCamera();

        return () => {
            if (videoRef.current?.srcObject) {
                (videoRef.current.srcObject as MediaStream)
                    .getTracks()
                    .forEach((track) => track.stop());
            }
        };
    }, [showTestModal]);

    const handleTestphotoClick = () => {
        setShowTestModal(true);
        setIsTestImageCaptured(false);
        setCurrentPredictions([]);
        setLevelStep(2); // Geht zum Testmodal (Frame 31)
    };

    const captureAndClassifyTestImage = async () => {
        if (!model || !videoRef.current || !canvasRef.current || !videoReady || videoRef.current.videoWidth === 0 || videoRef.current.videoHeight === 0) {
            console.warn("Kann kein Bild aufnehmen: Modell nicht geladen, Kamera nicht bereit oder Video-Dimensionen 0.");
            return;
        }

        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        canvas.width = videoRef.current.videoWidth;
        canvas.height = videoRef.current.videoHeight;
        ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);

        setCapturedImageUrl(canvas.toDataURL('image/png'));

        let imgTensor = tf.browser.fromPixels(canvas);
        imgTensor = tf.image.resizeBilinear(imgTensor, [224, 224]).div(255).expandDims(0);

        const predictionTensor = model.predict(imgTensor) as tf.Tensor;
        const predictionData = await predictionTensor.data();
        tf.dispose([imgTensor, predictionTensor]);

        const predictions = labels.map((label, i) => ({
            label,
            confidence: predictionData[i],
        })).sort((a, b) => b.confidence - a.confidence);

        setCurrentPredictions(predictions);
        setShowTestModal(false);
        setShowAnalyzeModal(true);

        setTimeout(() => {
            setShowAnalyzeModal(false);
            setShowResultModal(true);
            setLevelStep(3);
        }, 1500);
    };

    const handleResultModalClose = () => {
        setShowResultModal(false);
        setLevelStep(4); // Go to "Wusstet ihr schon?" dialog
        // Do NOT clear capturedImageUrl or currentPredictions here!
    };

    const handleQuizComplete = (passed: boolean) => {
        console.log("Quiz beendet! Bestanden:", passed);
        setLevelStep(6); // Geht zum Abschluss nach dem Quiz
    };

    const [showPasswordModal, setShowPasswordModal] = useState(!unlocked);
    const [passwordInput, setPasswordInput] = useState("");
    const [passwordError, setPasswordError] = useState("");

    // Passwort-Modal
    if (showPasswordModal) {
        return (
            <div style={{
                position: "fixed",
                top: 0,
                left: 0,
                width: "100vw",
                height: "100vh",
                background: "rgba(0,0,0,0.7)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 2000
            }}>
                <div style={{
                    backgroundColor: "#fff",
                    borderRadius: "15px",
                    padding: "40px",
                    boxShadow: "0 8px 16px rgba(0,0,0,0.2)",
                    minWidth: "320px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "20px"
                }}>
                    <h2 style={{ marginBottom: 10, color: "#333" }}>Passwort eingeben für Level 6</h2>
                    <p style={{ color: "#555", fontSize: "1.1em", textAlign: "center" }}>
                        Gebt hier das Passwort ein, um zu Level 6 zu gelangen:
                    </p>
                    <input
                        type="password"
                        value={passwordInput}
                        onChange={e => { setPasswordInput(e.target.value); setPasswordError(""); }}
                        style={{
                            padding: "10px",
                            fontSize: "1.2em",
                            borderRadius: "8px",
                            border: "1px solid #ccc",
                            width: "200px",
                            marginBottom: "10px"
                        }}
                        autoFocus
                        onKeyDown={e => {
                            if (e.key === "Enter") {
                                if (passwordInput.trim().toUpperCase() === LEVEL6_PASSWORD) {
                                    setShowPasswordModal(false);
                                    unlockLevel && unlockLevel();
                                } else {
                                    setPasswordError("Falsches Passwort");
                                }
                            }
                        }}
                    />
                    <div style={{
                        display: "flex",
                        justifyContent: "space-between",
                        width: "100%",
                        gap: "15px"
                    }}>
                        <button
                            onClick={onBack}
                            style={{
                                backgroundColor: "#f0f0f0",
                                color: "#555",
                                border: "1px solid #ccc",
                                borderRadius: "8px",
                                padding: "10px 25px",
                                fontSize: "1.1em",
                                cursor: "pointer"
                            }}
                        >
                            Abbrechen
                        </button>
                        <button
                            onClick={() => {
                                if (passwordInput.trim().toUpperCase() === LEVEL6_PASSWORD) {
                                    setShowPasswordModal(false);
                                    unlockLevel && unlockLevel();
                                } else {
                                    setPasswordError("Falsches Passwort");
                                }
                            }}
                            style={{
                                backgroundColor: "#4c84ff",
                                color: "white",
                                border: "none",
                                borderRadius: "8px",
                                padding: "10px 25px",
                                fontSize: "1.1em",
                                cursor: "pointer"
                            }}
                        >
                            Bestätigen
                        </button>
                    </div>
                    {passwordError && (
                        <div style={{ color: "red", fontWeight: 600 }}>{passwordError}</div>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div style={{
            fontFamily: 'Nunito, sans-serif',
            backgroundColor: '#f7f9fc',
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'flex-start',
            padding: '30px',
            gap: '30px',
        }}>
            <div style={{
                flexGrow: 1,
                backgroundColor: '#fff',
                borderRadius: '15px',
                padding: '30px',
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.05)',
                position: 'relative', // Needed for absolute positioning of the button
            }}>
                {/* Restart Button top right INSIDE the white card, styled like Level 1 */}
                <button
                    onClick={onRestart}
                    style={{
                        position: 'absolute',
                        top: 15,
                        right: 15,
                        backgroundColor: '#f0f0f0',
                        color: '#555',
                        border: '1px solid #ccc',
                        borderRadius: '8px',
                        padding: '8px 18px',
                        cursor: 'pointer',
                        fontSize: '1.1em',
                        fontWeight: 600,
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                        transition: 'background-color 0.2s ease',
                        zIndex: 10,
                    }}
                    onMouseOver={e => e.currentTarget.style.backgroundColor = '#e0e0e0'}
                    onMouseOut={e => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                >
                    🔄 Level neu starten
                </button>

                {/* Header-Bereich mit Avatar und Level-Titel */}
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '30px' }}>
                    <div style={{
                        width: '120px',
                        height: '120px',
                        borderRadius: '50%',
                        overflow: 'hidden',
                        border: '4px solid #4c84ff',
                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                        marginRight: '20px',
                        flexShrink: 0,
                    }}>
                        <img src={avatarImage} alt="Team Avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                       
                    </div>
                    <div style={{ flexGrow: 1 }}>
                        <h2 style={{ fontSize: '2.5em', fontWeight: '800', color: '#333', marginBottom: '10px' }}>
                            Level 6: Beiß rein!
                        </h2>
                        <p style={{ fontSize: '1.4em', color: '#555' }}>
                            KIs können Objekte auch erkennen, wenn sie verändert sind.
                        </p>
                    </div>
                </div>

                {/* Phasenabhängiger Inhalt */}
                {levelStep === 0 && ( /* Frame 29: Einführungstext */
                    <div style={{ ...contentBoxStyle }}>
                        <p style={{ ...paragraphStyle }}>
                            Beiß in einen Apfel oder schäl die Banane. Sie sehen jetzt anders aus, oder? Aber du weißt trotzdem, was sie sind! Teste mich und finde heraus, ob ich das mit Hilfe meiner Trainingsdaten auch erkennen kann. 
                        </p>
                        <div style={{ width: "100%", display: "flex", justifyContent: "flex-end" }}>
                            <button onClick={() => setLevelStep(1)} style={{ ...buttonStyle }}>
                                Weiter
                            </button>
                        </div>
                    </div>
                )}

                {levelStep === 1 && (
                    <div style={{ ...contentBoxStyle }}>
                        <p style={{ ...paragraphStyle }}>
                            Fotografiert euren angebissenen Apfel oder eure geschälte Banane. 📷 Sprecht danach mit der Klasse darüber, was ihr herausgefunden habt!
                        </p>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            width: '100%',
                            gap: '10px',
                            marginTop: '20px',
                        }}>
                            <button
                                onClick={() => setLevelStep(0)}
                                style={{
                                    ...buttonStyle,
                                    backgroundColor: '#f0f0f0',
                                    color: '#555',
                                    border: '1px solid #ccc',
                                }}
                                onMouseOver={e => e.currentTarget.style.backgroundColor = '#e0e0e0'}
                                onMouseOut={e => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                            >
                                Zurück
                            </button>
                            <button onClick={handleTestphotoClick} style={{ ...buttonStyle }}>
                                Weiter
                            </button>
                        </div>
                    </div>
                )}

                {levelStep === 4 && (
                    <div style={{ ...contentBoxStyle }}>
                        <h3 style={{ ...headingStyle, marginBottom: '10px' }}>
                            Wusstet ihr schon?
                        </h3>
                        <h4 style={{ ...headingStyle, fontSize: '1.5em', fontWeight: '600', marginBottom: '10px' }}>
                            {knewItTextLevel6.question}
                        </h4>
                        <p style={{ ...paragraphStyle }}>
                            {knewItTextLevel6.explanation}
                        </p>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            width: '100%',
                            gap: '10px',
                            marginTop: '20px',
                        }}>
                            <button
                                onClick={() => {
                                    setShowResultModal(true);
                                    setLevelStep(3);
                                }}
                                style={{
                                    ...buttonStyle,
                                    backgroundColor: '#f0f0f0',
                                    color: '#555',
                                    border: '1px solid #ccc',
                                }}
                                onMouseOver={e => e.currentTarget.style.backgroundColor = '#e0e0e0'}
                                onMouseOut={e => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                            >
                                Zurück
                            </button>
                            <button onClick={() => setLevelStep(5)} style={{ ...buttonStyle }}>
                                Weiter
                            </button>
                        </div>
                    </div>
                )}

                {/* {levelStep === 5 && ( 
                    <div style={{ ...contentBoxStyle }}>
                        <h3 style={{ ...headingStyle, marginBottom: '20px', textAlign: 'center' }}>
                            Testet euer Wissen!
                        </h3>
                        <p style={{ ...paragraphStyle, textAlign: 'center' }}>
                            Beantwortet die folgenden Fragen, um zu zeigen, was ihr über KIs und neuronale Netze gelernt habt!
                        </p>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            width: '100%',
                            gap: '10px',
                            marginTop: '20px',
                        }}>
                            <button
                                onClick={() => setLevelStep(4)}
                                style={{
                                    ...buttonStyle,
                                    backgroundColor: '#f0f0f0',
                                    color: '#555',
                                    border: '1px solid #ccc',
                                }}
                                onMouseOver={e => e.currentTarget.style.backgroundColor = '#e0e0e0'}
                                onMouseOut={e => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                            >
                                Zurück
                            </button>
                            <button onClick={() => setLevelStep(6)} style={{ ...buttonStyle }}>
                                Quiz starten
                            </button>
                        </div>
                    </div>
                )} */}

                    {/* Ehemals 6 */}
                {levelStep === 5 && ( /* Frame 34: Quiz anzeigen */
                    <CheckupQuiz
                        question1={quizQuestion1}
                        question2={quizQuestion2}
                        onQuizComplete={handleQuizComplete}
                        onBack={() => setLevelStep(4)} // Go back to previous step
                    />
                )}

                {/* Ehemals 7 */}
                {levelStep === 6 && ( /* Abschluss */
                    <div style={{ ...contentBoxStyle }}>
                        <p style={{ ...paragraphStyle }}>
                            Gruppe "{groupName}", das habt ihr super gemacht!
                            Ihr habt mich jetzt trainiert. Ich hoffe, ihr habt verstanden, wie ich lerne, um Dinge zu erkennen und was das Ergebnis verändert.
                            <br /><br />
                            Danke für eure Hilfe! 🤗
                        </p>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            width: '100%',
                            gap: '10px',
                            marginTop: '20px',
                        }}>
                            <button
                                onClick={() => setLevelStep(5)}
                                style={{
                                    ...buttonStyle,
                                    backgroundColor: '#f0f0f0',
                                    color: '#555',
                                    border: '1px solid #ccc',
                                }}
                                onMouseOver={e => e.currentTarget.style.backgroundColor = '#e0e0e0'}
                                onMouseOut={e => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                            >
                                Zurück
                            </button>
                            {/* You can add a "Fertig" or "Beenden" button here if needed */}
                        </div>
                    </div>
                )}


                {/* Test-Modal (Frame 31) */}
                {showTestModal && (
                    <div style={{ ...modalOverlayStyle }}>
                        <div style={{ ...modalContentStyle }}>
                            <h3 style={{ ...headingStyle, marginBottom: '15px', textAlign: 'center' }}>
                                Level 6: Neues Testbild aufnehmen
                            </h3>
                            <p style={{ ...paragraphStyle, textAlign: 'center' }}>
                                Nehmt ein neues Testbild auf.
                            </p>
                            <video
                                ref={videoRef}
                                style={{ ...videoStyle, display: videoReady ? 'block' : 'none' }}
                                autoPlay
                                muted
                                playsInline
                            />
                            {!videoReady && (
                                <div style={{ ...videoPlaceholderStyle }}>
                                    Kamera wird geladen...
                                </div>
                            )}

                            <div style={{display: 'flex', gap: '20px', marginTop: '20px'}}>
                                <button onClick={() => {
                                    setShowTestModal(false);
                                    setLevelStep(1);
                                }} style={{...buttonStyle, backgroundColor: '#d9534f'}}
                                        onMouseOver={e => e.currentTarget.style.backgroundColor = '#c9302c'}
                                        onMouseOut={e => e.currentTarget.style.backgroundColor = '#d9534f'}>
                                    Abbrechen
                                </button>
                                <button onClick={captureAndClassifyTestImage} disabled={!videoReady || loading} style={{
                                    ...buttonStyle,
                                    backgroundColor: '#4c84ff',
                                    cursor: (!videoReady || loading) ? 'not-allowed' : 'pointer',
                                    opacity: (!videoReady || loading) ? 0.6 : 1,
                                }}
                                        onMouseOver={e => !(!videoReady || loading) && (e.currentTarget.style.backgroundColor = '#3b6dd6')}
                                        onMouseOut={e => !(!videoReady || loading) && (e.currentTarget.style.backgroundColor = '#4c84ff')}>
                                    Weiter
                                    <img src="/icons/picture.png" alt="Upload"
                                         style={{width: '20px', height: '20px', marginLeft: '5px'}}/>
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Ergebnis-Modal (Frame 58 & 32) */}
                {showResultModal && (
                    <div style={{...modalOverlayStyle}}>
                        <div style={{...modalContentStyle, maxWidth: '500px'}}>
                            <h3 style={{...headingStyle, textAlign: 'center'}}>
                                Ergebnis:
                            </h3>
                            {/* Falls du ein Icon anzeigen möchtest, füge den korrekten Pfad hier ein */}
                            {/* <img src="/path/to/your_icon.png" alt="Testobjekt" style={{ maxWidth: '150px', marginBottom: '15px' }} /> */}
                            {/* Eingefügtes aufgenommenes Bild */}
                            {capturedImageUrl && (
                                <img
                                    src={capturedImageUrl}
                                    alt="Aufgenommenes Bild"
                                    style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'contain', marginBottom: '15px', border: '2px solid #ddd', borderRadius: '8px' }}
                                />
                            )}
                            {currentPredictions.map((prediction, index) => (
                                <div key={index} style={{ width: '100%', marginBottom: '10px' }}>
                                    <p style={{ fontSize: '1.1em', color: '#555', marginBottom: '5px' }}>
                                        {prediction.label}: {Math.round(prediction.confidence * 100)}%
                                    </p>
                                    <div style={{
                                        width: '100%',
                                        backgroundColor: '#f0f0f0',
                                        borderRadius: '5px',
                                        height: '15px',
                                        overflow: 'hidden',
                                    }}>
                                        <div style={{
                                            width: `${prediction.confidence * 100}%`,
                                            height: '100%',
                                            backgroundColor: prediction.label === "Apfel" ? '#FF6347' : '#FFD700', // Beispiel für Farben
                                            borderRadius: '5px',
                                        }} />
                                    </div>
                                </div>
                            ))}

                            {/* Warnhinweis */}
                            <p style={{
                                color: 'red',
                                fontWeight: 'bold',
                                fontSize: '1.1em',
                                marginTop: '10px',
                                textAlign: 'center',
                            }}>
                                Macht einen Screenshot vom Ergebnis!
                            </p>

                            {/* Footer with two buttons */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                width: '100%',
                gap: '15px',
                marginTop: '10px'
            }}>
                <button
                    onClick={() => {
                        setShowResultModal(false);
                        setCapturedImageUrl(null);
                        setCurrentPredictions([]);
                        setShowTestModal(true);
                        setVideoReady(false);
                    }}
                    style={{
                        backgroundColor: '#f0f0f0',
                        color: '#555',
                        border: '1px solid #ccc',
                        borderRadius: '8px',
                        padding: '12px 25px',
                        cursor: 'pointer',
                        fontSize: '1.1em',
                        fontWeight: 600,
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                        transition: 'background-color 0.2s ease',
                    }}
                    onMouseOver={e => e.currentTarget.style.backgroundColor = '#e0e0e0'}
                    onMouseOut={e => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                >
                    Nochmal ausprobieren
                </button>
                <button onClick={handleResultModalClose} style={{
                    backgroundColor: '#4c84ff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '12px 25px',
                    cursor: 'pointer',
                    fontSize: '1.1em',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                    transition: 'background-color 0.2s ease',
                }} onMouseOver={e => e.currentTarget.style.backgroundColor = '#3b6dd6'} onMouseOut={e => e.currentTarget.style.backgroundColor = '#4c84ff'}>
                    Weiter
                </button>
            </div>
        </div>
    </div>
)}

                {showAnalyzeModal && (
                    <div style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100vw",
                        height: "100vh",
                        background: "rgba(0,0,0,0.6)",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        zIndex: 1000,
                    }}>
                        <div style={{
                            backgroundColor: '#fff',
                            borderRadius: '15px',
                            padding: '40px',
                            boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)',
                            maxWidth: '600px',
                            width: '90%',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '25px',
                            position: 'relative',
                        }}>
                            <h3 style={{ fontSize: '1.8em', color: '#333', marginBottom: '15px', textAlign: 'center' }}>
                                Analysiere Bild...
                            </h3>
                            <div style={{ width: '80%', height: '10px', backgroundColor: '#f0f0f0', borderRadius: '5px' }}>
                                <div style={{
                                    width: '100%',
                                    height: '100%',
                                    backgroundColor: '#4c84ff',
                                    borderRadius: '5px',
                                    animation: 'loading-bar 1.5s forwards linear',
                                }} />
                            </div>
                            <style>
                                {`
                                @keyframes loading-bar {
                                    0% { width: 0%; }
                                    100% { width: 100%; }
                                }
                                `}
                            </style>
                        </div>
                    </div>
                )}

                <canvas ref={canvasRef} style={{ display: "none" }} />
            </div>
        </div>
    );
}

// Gemeinsame Stile
const contentBoxStyle = {
    display: 'flex',
    flexDirection: 'column' as 'column',
    alignItems: 'flex-start',
    gap: '20px',
    backgroundColor: '#e6f7ff',
    border: '2px solid #91d1ff',
    borderRadius: '10px',
    padding: '25px',
    maxWidth: '700px',
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
    marginTop: '30px',
};

const paragraphStyle = {
    fontSize: '1.3em',
    color: '#555',
    lineHeight: '1.6',
};

const headingStyle = {
    fontSize: '1.8em',
    color: '#333',
};

const buttonStyle = {
    marginTop: '20px',
    backgroundColor: '#4c84ff',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '12px 25px',
    cursor: 'pointer',
    fontSize: '1.1em',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    transition: 'background-color 0.2s ease',
};

const inputStyle = {
    width: 'calc(100% - 20px)',
    padding: '12px 10px',
    fontSize: '1.1em',
    border: '1px solid #ccc',
    borderRadius: '8px',
    boxSizing: 'border-box' as 'border-box',
    marginBottom: '10px',
};

const modalOverlayStyle = {
    position: "fixed" as 'fixed',
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    background: "rgba(0,0,0,0.6)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
};

const modalContentStyle = {
    backgroundColor: '#fff',
    borderRadius: '15px',
    padding: '40px',
    boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)',
    maxWidth: '600px',
    width: '90%',
    display: 'flex',
    flexDirection: 'column' as 'column',
    alignItems: 'center',
    gap: '25px',
    position: 'relative' as 'relative',
};

const videoStyle = {
    border: "3px solid #ddd",
    borderRadius: '10px',
    maxWidth: '100%',
    maxHeight: '400px',
};

const videoPlaceholderStyle = {
    width: '100%',
    height: '300px',
    backgroundColor: '#eee',
    borderRadius: '10px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    color: '#888',
};